/**
 * /kiosk — public front-counter iPad page
 * ────────────────────────────────────────
 * SINGLE-SCREEN layout:
 *   1. "Find your booking" search box at the top — type mobile / name / rego.
 *   2. Matching booking results render inline.
 *   3. "Or — book in as a walk-in" form below uses the SAME field schema as
 *      the SM's QuickBookingModal so all three sources (kiosk walk-in,
 *      kiosk search confirm, popup widget) write identical booking rows to
 *      the single `bookings` collection.
 *
 * The `Confirm` step for an existing booking is still its own screen because
 * the SM may need to flip vehicles / load existing details — but the
 * walk-in flow is inline so a fresh customer never has to click a button to
 * see the form.
 *
 * No auth required — exposed at /kiosk for the workshop iPad.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Search,
  Car,
  CheckCircle2,
  Loader2,
  ArrowLeft,
  LogOut,
  User,
  UserPlus,
  Plus,
  Phone,
  CalendarClock,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatAUMobile, formatRego } from '@/lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Identical to QuickBookingModal so every source writes the same shape.
const SERVICE_TYPES = [
  { value: 'service', label: 'Service' },
  { value: 'rwc', label: 'RWC' },
  { value: 'tyres', label: 'Tyres' },
  { value: 'aircon', label: 'Aircon' },
  { value: 'brakes', label: 'Brakes' },
  { value: 'suspension', label: 'Suspension' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'other', label: 'Other' },
];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
  } catch (_e) { return iso; }
};

const todayPlus = (days = 1) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const blankWalkIn = () => ({
  customer_first_name: '',
  customer_last_name: '',
  customer_phone: '',
  customer_email: '',
  customer_address: '',
  vehicle_rego: '',
  vehicle_make: '',
  vehicle_model: '',
  vehicle_year: '',
  vehicle_kms: '',
  vehicle_colour: '',
  vehicle_vin: '',
  scheduled_date: todayPlus(0),
  scheduled_time: '09:00',
  service_types: [],
  license_number: '',
  loan_car_requested: false,
  notes: '',
});

export default function Kiosk() {
  const navigate = useNavigate();
  // True only when an SM/admin is browsing the kiosk preview from inside
  // the app. Customer-facing iPads (not logged in) get no exit button —
  // they should be locked to the kiosk.
  const [isStaff, setIsStaff] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (r.ok) {
          const u = await r.json();
          setIsStaff(['super_admin', 'admin', 'service_manager', 'floor_manager'].includes(u?.role));
        }
      } catch (_e) { /* anonymous — leave hidden */ }
    })();
  }, []);

  // ── Exit-Kiosk PIN gate ─────────────────────────────────────────
  // Even logged-in staff need to type the kiosk PIN to exit, so a
  // customer who picks up the iPad while the SM is helping someone
  // else cannot leave the kiosk by tapping Exit. PIN is fetched from
  // the public site-settings endpoint; if no PIN is set on the site,
  // the Exit button is hidden entirely (most secure default).
  const [exitPin, setExitPin] = useState(null);  // null = not loaded; '' = no PIN configured
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [exitPinInput, setExitPinInput] = useState('');
  const [exitPinError, setExitPinError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/kiosk/exit-pin-status`, { credentials: 'omit' });
        if (r.ok) {
          const d = await r.json();
          setExitPin(d.pin_configured ? '*' : '');  // '*' = configured (value stays on server)
        } else {
          setExitPin('');
        }
      } catch (_e) { setExitPin(''); }
    })();
  }, []);

  const submitExitPin = async () => {
    setExitPinError('');
    if (!exitPinInput || exitPinInput.length < 4) {
      setExitPinError('Enter the 4+ digit PIN');
      return;
    }
    try {
      const r = await fetch(`${BACKEND_URL}/api/kiosk/exit-pin-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify({ pin: exitPinInput }),
      });
      if (r.ok) {
        const d = await r.json();
        if (d.ok) {
          setShowExitDialog(false);
          setExitPinInput('');
          navigate('/bookings');
          return;
        }
      }
      setExitPinError('Incorrect PIN — ask a Service Manager');
      setExitPinInput('');
    } catch (_e) {
      setExitPinError('Could not verify — try again');
    }
  };
  const [step, setStep] = useState('search'); // search → confirm → done

  // ── Workshop (site) binding — iter222 ───────────────────────────
  // Multi-site: walk-in bookings must carry the kiosk's site_id. The
  // device binds once: URL ?site=<site_id> wins (staff setup link),
  // else the stored binding, else auto-bind when only one site exists,
  // else show a one-time picker.
  const [kioskSiteId, setKioskSiteId] = useState(() => {
    try { return localStorage.getItem('kiosk_site_id') || ''; } catch (_e) { return ''; }
  });
  const [siteOptions, setSiteOptions] = useState([]);
  const [showSitePicker, setShowSitePicker] = useState(false);
  const bindSite = (sid) => {
    setKioskSiteId(sid);
    try { localStorage.setItem('kiosk_site_id', sid); } catch (_e) { /* noop */ }
    setShowSitePicker(false);
  };
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/kiosk/sites`, { credentials: 'omit' });
        if (!r.ok) return;
        const d = await r.json();
        const sites = d.sites || [];
        setSiteOptions(sites);
        const urlSite = new URLSearchParams(window.location.search).get('site');
        if (urlSite && sites.some(s => s.site_id === urlSite)) { bindSite(urlSite); return; }
        let stored = '';
        try { stored = localStorage.getItem('kiosk_site_id') || ''; } catch (_e) { /* noop */ }
        if (stored && sites.some(s => s.site_id === stored)) { setKioskSiteId(stored); return; }
        if (sites.length === 1) { bindSite(sites[0].site_id); return; }
        if (sites.length > 1) setShowSitePicker(true);
      } catch (_e) { /* offline — walk-in will surface the error */ }
    })();
     
  }, []);
  const kioskSiteName = siteOptions.find(s => s.site_id === kioskSiteId)?.name || '';

  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);

  // Existing-booking confirmation flow state
  const [custForm, setCustForm] = useState({
    first_name: '', last_name: '', mobile: '', email: '',
    address_line1: '', suburb: '', postcode: '', state: 'VIC',
  });
  const [vehForm, setVehForm] = useState({
    rego: '', make: '', model: '', year: '', vin: '', kms: '', colour: '',
  });
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newVeh, setNewVeh] = useState({
    rego: '', make: '', model: '', year: '', vin: '', kms: '', colour: '',
  });
  const [bookingForm, setBookingForm] = useState({
    scheduled_date: '', loan_car_requested: false, comments: '', license_number: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Walk-in form state — field names match QuickBookingModal.
  const [walkIn, setWalkIn] = useState(blankWalkIn());
  const [walkInSaving, setWalkInSaving] = useState(false);

  // Debounced search
  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/kiosk/search?q=${encodeURIComponent(query.trim())}`);
        if (res.ok && !cancelled) {
          const data = await res.json();
          setResults(data.results || []);
        }
      } catch (_e) {/* noop */}
      finally { if (!cancelled) setSearching(false); }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const pickBooking = (b) => {
    setPicked(b);
    setCustForm({
      first_name: b.customer.first_name || '',
      last_name: b.customer.last_name || '',
      mobile: b.customer.mobile || '',
      email: b.customer.email || '',
      address_line1: b.customer.address_line1 || '',
      suburb: b.customer.suburb || '',
      postcode: b.customer.postcode || '',
      state: b.customer.state || 'VIC',
    });
    setSelectedVehicleId(b.vehicle.vehicle_id);
    setVehForm({
      rego: b.vehicle.rego || '',
      make: b.vehicle.make || '',
      model: b.vehicle.model || '',
      year: b.vehicle.year || '',
      vin: b.vehicle.vin || '',
      kms: b.vehicle.kms || '',
      colour: b.vehicle.colour || '',
    });
    let schedLocal = '';
    if (b.scheduled_date) {
      try {
        const d = new Date(b.scheduled_date);
        const pad = (n) => String(n).padStart(2, '0');
        schedLocal = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      } catch (_e) { /* noop */ }
    }
    setBookingForm({
      scheduled_date: schedLocal,
      loan_car_requested: !!b.loan_car_requested,
      comments: '',
      license_number: '',
    });
    setStep('confirm');
  };

  const onVehicleSwitch = (vid) => {
    setSelectedVehicleId(vid);
    const v = picked.customer_vehicles.find(x => x.vehicle_id === vid) || picked.vehicle;
    setVehForm({
      rego: v.rego || '', make: v.make || '', model: v.model || '',
      year: v.year || '', vin: v.vin || '', kms: v.kms || '', colour: v.colour || '',
    });
    setAdding(false);
  };

  const submitCheckIn = async () => {
    if (!custForm.mobile?.trim()) { toast.error('Mobile is required'); return; }
    if (!custForm.email?.trim()) { toast.error('Please enter your email'); return; }
    if (!custForm.postcode?.trim()) { toast.error('Please enter your postcode'); return; }
    if (adding) {
      if (!newVeh.rego?.trim()) { toast.error('Enter the rego of the new vehicle'); return; }
    } else {
      if (!vehForm.make?.trim() || !vehForm.model?.trim()) {
        toast.error('Please confirm vehicle make + model');
        return;
      }
    }
    // iter202 — RWC bookings must capture the driver's licence number at
    // check-in (needed on the roadworthy certificate).
    const isRwcCheckIn = Array.isArray(picked?.service_types) && picked.service_types.includes('rwc');
    if (isRwcCheckIn && !bookingForm.license_number?.trim()) {
      toast.error("Please enter your driver's licence number — it's needed for the roadworthy certificate");
      return;
    }
    setSubmitting(true);
    try {
      let schedISO = null;
      if (bookingForm.scheduled_date) {
        try { schedISO = new Date(bookingForm.scheduled_date).toISOString(); } catch (_e) { /* noop */ }
      }
      const payload = {
        customer_updates: custForm,
        vehicle_updates: adding ? null : vehForm,
        selected_vehicle_id: adding ? null : selectedVehicleId,
        new_vehicle: adding ? newVeh : null,
        scheduled_date: schedISO,
        loan_car_requested: !!bookingForm.loan_car_requested,
        comments: (bookingForm.comments || '').trim() || null,
        customer_license_number: (bookingForm.license_number || '').trim().toUpperCase() || null,
      };
      const res = await fetch(`${BACKEND_URL}/api/kiosk/job-cards/${picked.job_card_id}/check-in`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setStep('done');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Check-in failed — please ask the front desk for help');
      }
    } catch (_e) {
      toast.error('Network error — please try again');
    } finally { setSubmitting(false); }
  };

  // ── Walk-in submit — posts payload shaped identically to QuickBookingModal
  // so the bookings table stays uniform regardless of source. Mandatory
  // fields enforced (iter115): first_name + last_name + mobile + address
  // + make + model + rego — same set the public web-booking page enforces,
  // so check-in vs online booking record identical data.
  const submitWalkIn = async () => {
    const missing = [];
    if (!walkIn.customer_first_name.trim()) missing.push('First name');
    if (!walkIn.customer_last_name.trim()) missing.push('Last name');
    if (!walkIn.customer_phone.trim()) missing.push('Mobile');
    if (!walkIn.customer_address.trim()) missing.push('Address');
    if (!walkIn.vehicle_make.trim()) missing.push('Make');
    if (!walkIn.vehicle_model.trim()) missing.push('Model');
    if (!walkIn.vehicle_rego.trim()) missing.push('Rego');
    if (!walkIn.service_types?.length) missing.push('Service type');
    if (walkIn.service_types?.includes('rwc') && !walkIn.license_number?.trim()) {
      missing.push("Driver's licence number (needed for RWC)");
    }
    if (missing.length) {
      toast.error(`Please fill in: ${missing.join(', ')}`);
      return;
    }

    let schedISO = null;
    if (walkIn.scheduled_date) {
      try {
        schedISO = new Date(`${walkIn.scheduled_date}T${walkIn.scheduled_time || '09:00'}`).toISOString();
      } catch (_e) { /* noop */ }
    }
    setWalkInSaving(true);
    try {
      const payload = {
        customer: {
          first_name: walkIn.customer_first_name.trim(),
          last_name: walkIn.customer_last_name.trim(),
          mobile: walkIn.customer_phone.trim(),
          email: walkIn.customer_email?.trim() || '',
          address: walkIn.customer_address.trim(),
        },
        vehicle: {
          rego: walkIn.vehicle_rego.trim().toUpperCase(),
          make: walkIn.vehicle_make.trim(),
          model: walkIn.vehicle_model.trim(),
          year: walkIn.vehicle_year ? Number(walkIn.vehicle_year) : null,
          kms: walkIn.vehicle_kms ? Number(walkIn.vehicle_kms) : null,
          colour: walkIn.vehicle_colour?.trim() || null,
          vin: walkIn.vehicle_vin?.trim().toUpperCase() || null,
        },
        scheduled_date: schedISO,
        loan_car_requested: !!walkIn.loan_car_requested,
        comments: walkIn.notes?.trim() || null,
        service_types: walkIn.service_types,
        service_type: walkIn.service_types[0],
        site_id: kioskSiteId || undefined,
        customer_license_number: walkIn.license_number?.trim().toUpperCase() || null,
      };
      const res = await fetch(`${BACKEND_URL}/api/kiosk/walk-in-booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        // iter128 — backend may return ok:false + duplicate:true when an
        // open JC already exists for this (rego, mobile). Surface a
        // clear "we found your booking" message so the customer doesn't
        // double-up. They should then ask the SM at the front desk.
        if (data && data.duplicate) {
          toast.error(
            data.message || 'An open booking already exists for this rego + mobile — please ask the front desk.',
            { duration: 10000 },
          );
          return;
        }
        setStep('done');
        setWalkIn(blankWalkIn());
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save booking — please ask the front desk');
      }
    } catch (_e) {
      toast.error('Network error — please try again');
    } finally { setWalkInSaving(false); }
  };

  const resetAll = () => {
    setStep('search'); setQuery(''); setResults([]); setPicked(null);
    setSelectedVehicleId(null); setAdding(false);
    setNewVeh({ rego: '', make: '', model: '', year: '', vin: '', kms: '', colour: '' });
    setBookingForm({ scheduled_date: '', loan_car_requested: false, comments: '', license_number: '' });
    setWalkIn(blankWalkIn());
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-white">
      <header className="border-b bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold">HK</div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Workshop Check-In</h1>
              <p className="text-[11px] text-slate-500" data-testid="kiosk-site-name">
                {kioskSiteName ? `${kioskSiteName} · ` : ''}Find your booking — or walk in below
              </p>
            </div>
          </div>
          {step !== 'search' && (
            <Button variant="ghost" size="sm" onClick={resetAll} data-testid="kiosk-back">
              <ArrowLeft className="h-4 w-4 mr-1" /> Start over
            </Button>
          )}
          {/* "Exit Kiosk" — shown ONLY when the site has an exit PIN
              configured AND the visitor is authenticated staff. Even
              then, clicking it triggers the PIN dialog so a customer
              who picks up the iPad mid-session cannot escape just by
              tapping the button. If the SM hasn't set a PIN yet
              (`exitPin === ''`), the button is hidden entirely —
              fail-closed default. */}
          {isStaff && exitPin === '*' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setShowExitDialog(true); setExitPinInput(''); setExitPinError(''); }}
              data-testid="kiosk-exit"
              className="text-slate-500 hover:text-slate-900 ml-2"
              title="Return to Bookings (PIN required)"
            >
              <LogOut className="h-4 w-4 mr-1" /> Exit Kiosk
            </Button>
          )}
        </div>
      </header>

      {/* One-time workshop binding — multi-site kiosks must know which
          workshop they belong to before accepting walk-ins. */}
      {showSitePicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="kiosk-site-picker">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-xl font-semibold text-slate-900 mb-1">Select this kiosk's workshop</h3>
            <p className="text-sm text-slate-500 mb-5">
              One-time setup — walk-in bookings will be created for the selected workshop.
            </p>
            <div className="space-y-2">
              {siteOptions.map(s => (
                <button
                  key={s.site_id}
                  onClick={() => bindSite(s.site_id)}
                  className="w-full text-left p-4 rounded-xl border-2 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/50 font-medium text-slate-800"
                  data-testid={`kiosk-site-option-${s.site_id}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* PIN dialog — full-screen overlay so the customer can't tap
          around it. Submits to the server (constant-time comparison)
          rather than checking client-side, so the PIN never leaves the
          backend. */}
      {showExitDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          data-testid="kiosk-exit-dialog"
          role="dialog"
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget) setShowExitDialog(false); }}
        >
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-xl font-semibold text-slate-900 mb-1">Exit Kiosk</h3>
            <p className="text-sm text-slate-500 mb-5">
              Enter the kiosk PIN to return to the staff app.
            </p>
            <Input
              type="password"
              inputMode="numeric"
              autoFocus
              value={exitPinInput}
              onChange={(e) => setExitPinInput(e.target.value.replace(/\D/g, '').slice(0, 12))}
              onKeyDown={(e) => { if (e.key === 'Enter') submitExitPin(); }}
              placeholder="••••"
              className="text-center tracking-[0.5em] text-2xl h-14"
              data-testid="kiosk-exit-pin-input"
            />
            {exitPinError && (
              <p className="text-sm text-rose-600 mt-2" data-testid="kiosk-exit-pin-error">
                {exitPinError}
              </p>
            )}
            <div className="flex gap-2 mt-5">
              <Button
                variant="outline"
                onClick={() => { setShowExitDialog(false); setExitPinInput(''); setExitPinError(''); }}
                data-testid="kiosk-exit-cancel"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={submitExitPin}
                data-testid="kiosk-exit-submit"
                className="flex-1 bg-slate-900 text-white hover:bg-slate-800"
              >
                Exit
              </Button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-3xl mx-auto px-6 py-8">
        {step === 'search' && (
          <SearchAndWalkIn
            query={query} setQuery={setQuery}
            searching={searching} results={results}
            onPick={pickBooking}
            walkIn={walkIn} setWalkIn={setWalkIn}
            walkInSaving={walkInSaving}
            onSubmitWalkIn={submitWalkIn}
          />
        )}
        {step === 'confirm' && picked && (
          <ConfirmStep
            picked={picked}
            custForm={custForm} setCustForm={setCustForm}
            vehForm={vehForm} setVehForm={setVehForm}
            selectedVehicleId={selectedVehicleId}
            onVehicleSwitch={onVehicleSwitch}
            adding={adding} setAdding={setAdding}
            newVeh={newVeh} setNewVeh={setNewVeh}
            bookingForm={bookingForm} setBookingForm={setBookingForm}
            submitting={submitting}
            onSubmit={submitCheckIn}
          />
        )}
        {step === 'done' && <DoneStep onAnother={resetAll} />}
      </main>
    </div>
  );
}

function SearchAndWalkIn({
  query, setQuery, searching, results, onPick,
  walkIn, setWalkIn, walkInSaving, onSubmitWalkIn,
}) {
  const w = (k, v) => setWalkIn(prev => ({ ...prev, [k]: v }));
  const toggleService = (val) => setWalkIn(prev => ({
    ...prev,
    service_types: prev.service_types.includes(val)
      ? prev.service_types.filter(x => x !== val)
      : [...prev.service_types, val],
  }));

  return (
    <div className="space-y-6">
      {/* Find Your Booking */}
      <Card className="p-6">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1">
          <Search className="h-4 w-4 text-emerald-600" />
          Find your booking
        </div>
        <p className="text-xs text-slate-500 mb-4">Type your mobile, name, or rego</p>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => {
              // Live-format AU mobile/landline input so customers see
              // exactly the canonical "0415 837 157" form they have on
              // file. When the input is pure digits we group as 4-3-3
              // for 10-digit numbers — matches DB format and triggers
              // the kiosk's exact-match phone search immediately.
              let v = e.target.value;
              const digits = v.replace(/\D/g, '');
              // Only auto-format if the user is clearly typing a phone
              // (no letters present + ≥4 digits typed). Names/regos pass
              // through untouched.
              const looksLikePhone = /^\s*[+\d\s]+\s*$/.test(v) && digits.length >= 4;
              if (looksLikePhone) {
                if (digits.startsWith('61') && digits.length >= 11) {
                  // +61 415 837 157 → 0415 837 157
                  const local = '0' + digits.slice(2);
                  v = local.slice(0, 4)
                    + (local.length > 4 ? ' ' + local.slice(4, 7) : '')
                    + (local.length > 7 ? ' ' + local.slice(7, 10) : '');
                } else if (digits.startsWith('0') && digits.length === 10) {
                  v = digits.slice(0, 4) + ' ' + digits.slice(4, 7) + ' ' + digits.slice(7, 10);
                } else if (digits.startsWith('0') && digits.length >= 4) {
                  v = digits.slice(0, 4)
                    + (digits.length > 4 ? ' ' + digits.slice(4, Math.min(digits.length, 7)) : '')
                    + (digits.length > 7 ? ' ' + digits.slice(7, 10) : '');
                }
              }
              setQuery(v);
            }}
            placeholder="e.g. 0411 123 456 / John Smith / ABC123"
            className="pl-10 h-12 text-base"
            data-testid="kiosk-search-input"
            inputMode="search"
            autoComplete="off"
            spellCheck={false}
          />
          {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-emerald-500 animate-spin" />}
        </div>

        {query.trim().length >= 2 && !searching && results.length === 0 && (
          <p className="text-sm text-slate-500 text-center py-4 mt-2" data-testid="kiosk-no-results">
            No booking found for today — use the walk-in form below.
          </p>
        )}
        {results.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="kiosk-results">
            {results.map(r => (
              <button
                key={r.job_card_id}
                onClick={() => onPick(r)}
                className="w-full text-left p-3 rounded-xl border-2 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/50 transition-all group"
                data-testid={`kiosk-result-${r.job_card_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <Car className="h-3.5 w-3.5 text-emerald-600" />
                      <span className="font-semibold text-slate-900 text-sm">
                        {r.vehicle.rego} · {r.vehicle.make} {r.vehicle.model}
                      </span>
                      {r.status === 'no_show' && (
                        <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0 rounded-full">missed</span>
                      )}
                      {r.status === 'pickup' && (
                        <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0 rounded-full">checked in</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600">
                      <User className="h-3 w-3 inline mr-1 text-slate-400" />{r.customer.display_name}
                      <Phone className="h-3 w-3 inline ml-2 mr-1 text-slate-400" />{r.customer.mobile}
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      <CalendarClock className="h-2.5 w-2.5 inline mr-1" />{fmtDate(r.scheduled_date)}
                    </p>
                  </div>
                  <ArrowLeft className="h-4 w-4 rotate-180 text-slate-300 group-hover:text-emerald-600 transition-colors" />
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>

      {/* Or — Walk-in */}
      <Card className="p-6 border-l-4 border-l-emerald-500">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1">
          <UserPlus className="h-4 w-4 text-emerald-600" />
          Or — walk-in booking
        </div>
        <p className="text-xs text-slate-500 mb-4">No prior booking? Fill this in instead.</p>

        <form onSubmit={(e) => { e.preventDefault(); onSubmitWalkIn(); }} className="space-y-4">
          {/* Customer */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              value={walkIn.customer_first_name}
              onChange={(e) => w('customer_first_name', e.target.value)}
              placeholder="First name *"
              className="h-11"
              data-testid="kiosk-wi-first-name"
              required
            />
            <Input
              value={walkIn.customer_last_name}
              onChange={(e) => w('customer_last_name', e.target.value)}
              placeholder="Last name *"
              className="h-11"
              data-testid="kiosk-wi-last-name"
              required
            />
            <Input
              type="tel"
              value={walkIn.customer_phone}
              onChange={(e) => {
                // Same live phone-format helper as the search input.
                let v = e.target.value;
                const digits = v.replace(/\D/g, '');
                if (/^\s*[+\d\s]+\s*$/.test(v) && digits.length >= 4) {
                  if (digits.startsWith('61') && digits.length >= 11) {
                    const local = '0' + digits.slice(2);
                    v = local.slice(0, 4)
                      + (local.length > 4 ? ' ' + local.slice(4, 7) : '')
                      + (local.length > 7 ? ' ' + local.slice(7, 10) : '');
                  } else if (digits.startsWith('0') && digits.length === 10) {
                    v = digits.slice(0, 4) + ' ' + digits.slice(4, 7) + ' ' + digits.slice(7, 10);
                  } else if (digits.startsWith('0') && digits.length >= 4) {
                    v = digits.slice(0, 4)
                      + (digits.length > 4 ? ' ' + digits.slice(4, Math.min(digits.length, 7)) : '')
                      + (digits.length > 7 ? ' ' + digits.slice(7, 10) : '');
                  }
                }
                w('customer_phone', v);
              }}
              placeholder="Mobile *"
              className="h-11"
              data-testid="kiosk-wi-mobile"
              required
            />
            <Input
              type="email"
              value={walkIn.customer_email}
              onChange={(e) => w('customer_email', e.target.value)}
              placeholder="Email (optional)"
              className="h-11"
              data-testid="kiosk-wi-email"
            />
            <Input
              value={walkIn.customer_address}
              onChange={(e) => w('customer_address', e.target.value)}
              placeholder="Address *"
              className="h-11 md:col-span-2"
              data-testid="kiosk-wi-address"
              required
            />
          </div>

          {/* Vehicle */}
          <div className="grid grid-cols-3 gap-3">
            <Input
              value={walkIn.vehicle_rego}
              onChange={(e) => w('vehicle_rego', formatRego(e.target.value))}
              placeholder="Rego *"
              maxLength={11}
              className="h-11 uppercase font-mono"
              data-testid="kiosk-wi-rego"
              required
            />
            <Input
              value={walkIn.vehicle_make}
              onChange={(e) => w('vehicle_make', e.target.value)}
              placeholder="Make *"
              className="h-11"
              data-testid="kiosk-wi-make"
              required
            />
            <Input
              value={walkIn.vehicle_model}
              onChange={(e) => w('vehicle_model', e.target.value)}
              placeholder="Model *"
              className="h-11"
              data-testid="kiosk-wi-model"
              required
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Input
              type="number"
              value={walkIn.vehicle_year}
              onChange={(e) => w('vehicle_year', e.target.value)}
              placeholder="Year"
              className="h-11"
              data-testid="kiosk-wi-year"
            />
            <Input
              type="number"
              value={walkIn.vehicle_kms}
              onChange={(e) => w('vehicle_kms', e.target.value)}
              placeholder="Odometer (kms)"
              className="h-11"
              data-testid="kiosk-wi-kms"
            />
            <Input
              value={walkIn.vehicle_colour}
              onChange={(e) => w('vehicle_colour', e.target.value)}
              placeholder="Colour"
              className="h-11"
              data-testid="kiosk-wi-colour"
            />
          </div>
          <Input
            value={walkIn.vehicle_vin}
            onChange={(e) => w('vehicle_vin', e.target.value.toUpperCase())}
            placeholder="VIN (17 chars, optional)"
            maxLength={17}
            className="h-11 uppercase font-mono text-xs"
            data-testid="kiosk-wi-vin"
          />

          {/* Scheduled date + time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[11px] text-slate-500 mb-1 ml-0.5">Drop-off date</p>
              <Input
                type="date"
                value={walkIn.scheduled_date}
                onChange={(e) => w('scheduled_date', e.target.value)}
                className="h-11"
                data-testid="kiosk-wi-sched-date"
              />
            </div>
            <div>
              <p className="text-[11px] text-slate-500 mb-1 ml-0.5">Time</p>
              <Input
                type="time"
                value={walkIn.scheduled_time}
                onChange={(e) => w('scheduled_time', e.target.value)}
                className="h-11"
                data-testid="kiosk-wi-sched-time"
              />
            </div>
          </div>

          {/* Service types */}
          <div>
            <p className="text-xs font-medium text-slate-700 mb-2">Service required *</p>
            <div className="flex flex-wrap gap-2" data-testid="kiosk-wi-service-types">
              {SERVICE_TYPES.map(t => {
                const sel = walkIn.service_types.includes(t.value);
                return (
                  <button
                    type="button"
                    key={t.value}
                    onClick={() => toggleService(t.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                      sel
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                    }`}
                    data-testid={`kiosk-wi-svc-${t.value}`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* iter202 — RWC walk-ins must leave their licence number for the
              roadworthy certificate. Only shows when RWC is selected. */}
          {walkIn.service_types.includes('rwc') && (
            <div className="p-3 rounded-lg border-2 border-amber-300 bg-amber-50" data-testid="kiosk-wi-license-block">
              <p className="text-sm font-semibold text-amber-900">Driver's licence number *</p>
              <p className="text-[11px] text-amber-700 mb-2">Required for your roadworthy certificate (RWC)</p>
              <Input
                value={walkIn.license_number}
                onChange={(e) => w('license_number', e.target.value.toUpperCase())}
                placeholder="e.g. 012345678"
                className="h-11 bg-white font-mono tracking-wide"
                data-testid="kiosk-wi-license-number"
              />
            </div>
          )}

          {/* Loan car */}
          <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-emerald-300 cursor-pointer transition">
            <Checkbox
              checked={walkIn.loan_car_requested}
              onCheckedChange={(v) => w('loan_car_requested', !!v)}
              className="mt-0.5"
              data-testid="kiosk-wi-loan-checkbox"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-800">I need a loan car</p>
              <p className="text-[11px] text-slate-500 mt-0.5">Tick if you'd like a courtesy vehicle — subject to availability.</p>
            </div>
          </label>

          {/* Notes */}
          <Textarea
            value={walkIn.notes}
            onChange={(e) => w('notes', e.target.value)}
            placeholder="Anything else we should know? (optional)"
            rows={3}
            className="text-sm"
            data-testid="kiosk-wi-notes"
          />

          <Button
            type="submit"
            size="lg"
            disabled={walkInSaving}
            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
            data-testid="kiosk-wi-submit"
          >
            {walkInSaving ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <CheckCircle2 className="h-5 w-5 mr-2" />}
            Confirm Booking
          </Button>
        </form>
      </Card>
    </div>
  );
}

function ConfirmStep({
  picked, custForm, setCustForm, vehForm, setVehForm,
  selectedVehicleId, onVehicleSwitch, adding, setAdding, newVeh, setNewVeh,
  bookingForm, setBookingForm,
  submitting, onSubmit,
}) {
  const cust = (k, v) => setCustForm(prev => ({ ...prev, [k]: v }));
  const veh = (k, v) => setVehForm(prev => ({ ...prev, [k]: v }));
  const nv = (k, v) => setNewVeh(prev => ({ ...prev, [k]: v }));
  const bkg = (k, v) => setBookingForm(prev => ({ ...prev, [k]: v }));

  const vehicleOptions = useMemo(() => picked.customer_vehicles || [], [picked]);
  const firstName = picked.customer.first_name
    || picked.customer.display_name?.split(' ')[0]
    || 'there';

  return (
    <div className="max-w-lg mx-auto">
      <div className="text-center mb-6">
        <div className="w-14 h-14 rounded-xl bg-emerald-600 flex items-center justify-center mx-auto mb-3">
          <CheckCircle2 className="h-7 w-7 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900">Hi {firstName} — confirm your details</h1>
        <p className="text-slate-500 text-sm mt-1">A quick top-up so we can hand the car back faster</p>
        <p className="text-[11px] text-slate-400 mt-1">
          Booked: <span className="font-medium text-slate-600">{picked.vehicle.rego}</span> · {picked.scheduled_date ? fmtDate(picked.scheduled_date) : '—'}
        </p>
      </div>

      <Card className="p-5">
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-5">

          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <User className="h-4 w-4 text-emerald-600" />
              Your Details
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input value={custForm.first_name} onChange={(e) => cust('first_name', e.target.value)} placeholder="First name *" className="h-11" data-testid="kiosk-first-name" />
              <Input value={custForm.last_name} onChange={(e) => cust('last_name', e.target.value)} placeholder="Last name *" className="h-11" data-testid="kiosk-last-name" />
              <Input type="tel" value={custForm.mobile} onChange={(e) => cust('mobile', formatAUMobile(e.target.value))} placeholder="Mobile *" className="h-11" data-testid="kiosk-mobile" />
              <Input type="email" value={custForm.email} onChange={(e) => cust('email', e.target.value)} placeholder="Email *" className="h-11" data-testid="kiosk-email" />
              <Input value={custForm.address_line1} onChange={(e) => cust('address_line1', e.target.value)} placeholder="Street address" className="h-11 col-span-2" data-testid="kiosk-address" />
              <Input value={custForm.suburb} onChange={(e) => cust('suburb', e.target.value)} placeholder="Suburb" className="h-11" data-testid="kiosk-suburb" />
              <div className="grid grid-cols-2 gap-3">
                <Input value={custForm.postcode} onChange={(e) => cust('postcode', e.target.value)} placeholder="Postcode *" className="h-11" data-testid="kiosk-postcode" />
                <Input value={custForm.state} onChange={(e) => cust('state', e.target.value)} placeholder="State" className="h-11 uppercase" data-testid="kiosk-state" />
              </div>
            </div>
          </div>

          {!adding && vehicleOptions.length > 1 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Car className="h-4 w-4 text-emerald-600" />
                Which vehicle today?
              </div>
              <div className="grid grid-cols-2 gap-2" data-testid="kiosk-vehicle-list">
                {vehicleOptions.map(v => (
                  <button
                    key={v.vehicle_id}
                    type="button"
                    onClick={() => onVehicleSwitch(v.vehicle_id)}
                    className={`p-2 rounded-lg border text-left text-xs transition ${
                      selectedVehicleId === v.vehicle_id
                        ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`kiosk-vehicle-${v.vehicle_id}`}
                  >
                    <p className="font-semibold text-slate-900">{v.rego}</p>
                    <p className="text-slate-500">{v.make} {v.model} {v.year || ''}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Car className="h-4 w-4 text-emerald-600" />
                {adding ? 'New Vehicle' : 'Vehicle Details'}
              </div>
              <button
                type="button"
                onClick={() => setAdding(a => !a)}
                className="text-xs text-emerald-700 hover:underline flex items-center gap-1"
                data-testid="kiosk-add-vehicle-toggle"
              >
                <Plus className="h-3 w-3" />
                {adding ? 'Use existing vehicle' : 'Different vehicle today'}
              </button>
            </div>

            {!adding ? (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <Input value={vehForm.rego} onChange={(e) => veh('rego', formatRego(e.target.value))} placeholder="Rego *" className="h-11 uppercase font-mono" maxLength={9} data-testid="kiosk-veh-rego" />
                  <Input value={vehForm.make} onChange={(e) => veh('make', e.target.value)} placeholder="Make *" className="h-11" data-testid="kiosk-veh-make" />
                  <Input value={vehForm.model} onChange={(e) => veh('model', e.target.value)} placeholder="Model *" className="h-11" data-testid="kiosk-veh-model" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Input type="number" value={vehForm.year} onChange={(e) => veh('year', e.target.value)} placeholder="Year" className="h-11" data-testid="kiosk-veh-year" />
                  <Input type="number" value={vehForm.kms} onChange={(e) => veh('kms', e.target.value)} placeholder="Odometer (kms)" className="h-11" data-testid="kiosk-veh-kms" />
                  <Input value={vehForm.colour} onChange={(e) => veh('colour', e.target.value)} placeholder="Colour" className="h-11" data-testid="kiosk-veh-colour" />
                </div>
                <Input value={vehForm.vin} onChange={(e) => veh('vin', e.target.value.toUpperCase())} placeholder="VIN (17 chars, optional)" className="h-11 uppercase font-mono text-xs" maxLength={17} data-testid="kiosk-veh-vin" />
              </>
            ) : (
              <div className="bg-amber-50/40 border border-amber-200 rounded-lg p-3 space-y-3">
                <p className="text-[11px] text-amber-700">Adding this to your vehicle list — pre-loaded for next visit too.</p>
                <div className="grid grid-cols-3 gap-3">
                  <Input value={newVeh.rego} onChange={(e) => nv('rego', formatRego(e.target.value))} placeholder="Rego *" className="h-11 uppercase font-mono" maxLength={9} data-testid="kiosk-newveh-rego" />
                  <Input value={newVeh.make} onChange={(e) => nv('make', e.target.value)} placeholder="Make" className="h-11" data-testid="kiosk-newveh-make" />
                  <Input value={newVeh.model} onChange={(e) => nv('model', e.target.value)} placeholder="Model" className="h-11" data-testid="kiosk-newveh-model" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Input type="number" value={newVeh.year} onChange={(e) => nv('year', e.target.value)} placeholder="Year" className="h-11" data-testid="kiosk-newveh-year" />
                  <Input type="number" value={newVeh.kms} onChange={(e) => nv('kms', e.target.value)} placeholder="Odometer (kms)" className="h-11" data-testid="kiosk-newveh-kms" />
                  <Input value={newVeh.colour} onChange={(e) => nv('colour', e.target.value)} placeholder="Colour" className="h-11" data-testid="kiosk-newveh-colour" />
                </div>
                <Input value={newVeh.vin} onChange={(e) => nv('vin', e.target.value.toUpperCase())} placeholder="VIN (17 chars, optional)" className="h-11 uppercase font-mono text-xs" maxLength={17} data-testid="kiosk-newveh-vin" />
              </div>
            )}
          </div>

          <div className="space-y-3 pt-2 border-t border-slate-100">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <CalendarClock className="h-4 w-4 text-emerald-600" />
              Booking Details
            </div>
            {/* iter138.9l — Show the service category the SM picked
                when they took the booking, so the customer sees exactly
                what they're confirming. Read-only — customer can't
                change it; the workshop already planned the day around
                that category. */}
            {Array.isArray(picked?.service_types) && picked.service_types.length > 0 && (
              <div data-testid="kiosk-checkin-services">
                <p className="text-[11px] text-slate-500 mb-1.5 ml-0.5">Service booked</p>
                <div className="flex flex-wrap gap-1.5">
                  {picked.service_types.map(st => {
                    const opt = SERVICE_TYPES.find(s => s.value === st);
                    return (
                      <span
                        key={st}
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
                        data-testid={`kiosk-checkin-svc-${st}`}
                      >
                        {opt?.label || st}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
            {Array.isArray(picked?.service_types) && picked.service_types.includes('rwc') && (
              <div className="p-3 rounded-lg border-2 border-amber-300 bg-amber-50" data-testid="kiosk-license-block">
                <p className="text-sm font-semibold text-amber-900">Driver's licence number *</p>
                <p className="text-[11px] text-amber-700 mb-2">Required for your roadworthy certificate (RWC)</p>
                <Input
                  value={bookingForm.license_number || ''}
                  onChange={(e) => bkg('license_number', e.target.value.toUpperCase())}
                  placeholder="e.g. 012345678"
                  className="h-11 bg-white font-mono tracking-wide"
                  data-testid="kiosk-license-number"
                />
              </div>
            )}
            <div>
              <p className="text-[11px] text-slate-500 mb-1 ml-0.5">Scheduled drop-off</p>
              <Input
                type="datetime-local"
                value={bookingForm.scheduled_date}
                onChange={(e) => bkg('scheduled_date', e.target.value)}
                className="h-11"
                data-testid="kiosk-booking-date"
              />
            </div>
            <label className="flex items-start gap-3 p-3 rounded-lg border-2 border-slate-200 hover:border-emerald-300 cursor-pointer transition">
              <Checkbox
                checked={bookingForm.loan_car_requested}
                onCheckedChange={(v) => bkg('loan_car_requested', !!v)}
                className="mt-0.5"
                data-testid="kiosk-loan-car-checkbox"
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-800">I need a loan car</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Tick if you'd like a courtesy vehicle — subject to availability.</p>
              </div>
            </label>
            <Textarea
              value={bookingForm.comments}
              onChange={(e) => bkg('comments', e.target.value)}
              placeholder="Anything you'd like the workshop to know?"
              className="min-h-[60px] text-sm"
              rows={2}
              data-testid="kiosk-comments"
            />
          </div>

          <Button
            type="submit"
            size="lg"
            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
            disabled={submitting}
            data-testid="kiosk-submit"
          >
            {submitting ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <CheckCircle2 className="h-5 w-5 mr-2" />}
            Confirm Booking
          </Button>
        </form>
      </Card>
    </div>
  );
}

function DoneStep({ onAnother }) {
  return (
    <Card className="p-10 text-center" data-testid="kiosk-done">
      <div className="mx-auto h-20 w-20 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
        <CheckCircle2 className="h-10 w-10 text-emerald-600" />
      </div>
      <h2 className="text-2xl font-bold text-slate-900 mb-2">You're checked in!</h2>
      <p className="text-slate-600 mb-6">Please leave your keys at the front desk. Our team will be with you shortly.</p>
      <Button size="lg" variant="outline" onClick={onAnother}>
        Check in another booking
      </Button>
    </Card>
  );
}
